import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Send,
  Search,
  Loader,
  Plus,
  MessageSquare,
  ArrowLeft,
  User,
  X,
} from "lucide-react";
import { toast } from "sonner";
import AutoGrowTextarea from "@/components/AutoGrowTextarea";
import linkifyText from "@/utils/linkifyText";

const API = process.env.REACT_APP_BACKEND_URL || "https://shootzu.com";

const getAdminHeaders = () => {
  const token = localStorage.getItem("shootzu_admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AVATAR_COLORS = [
  "#572B7C",
  "#0F766E",
  "#2563EB",
  "#78287C",
  "#15803D",
  "#D97706",
];

const ROLE_COLORS = {
  Photographer: "#572B7C",
  Videographer: "#0F766E",
  "Job Owner": "#2563EB",
  "Drone Operator": "#D97706",
};

const avatarColor = (key) => {
  const str = String(key || "");
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash + str.charCodeAt(i)) % 997;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const AdminMessages = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showNewConvoModal, setShowNewConvoModal] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const convPollRef = useRef(null);
  const msgPollRef = useRef(null);

  useEffect(() => {
    const isAdmin = localStorage.getItem("shootzu_admin");
    const token = localStorage.getItem("shootzu_admin_token");
    if (!isAdmin || !token) navigate("/szadmin");
  }, [navigate]);

  // Load conversations
  const loadConversations = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await axios.get(`${API}/api/admin/messages/conversations`, {
        params: { page: 1, limit: 50, search: searchQuery || undefined },
        headers: getAdminHeaders(),
      });
      setConversations(res.data.conversations || []);
    } catch (err) {
      console.error("Error loading conversations:", err);
      if (err.response?.status === 401) {
        navigate("/szadmin");
        return;
      }
      if (!silent) toast.error("Failed to load conversations");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Poll conversations every 10s
  useEffect(() => {
    convPollRef.current = setInterval(() => loadConversations(true), 10000);
    return () => clearInterval(convPollRef.current);
  }, [loadConversations]);

  // Load messages for selected conversation
  const loadMessages = useCallback(async (conversationId, silent = false) => {
    if (!conversationId) return;
    try {
      if (!silent) setMessagesLoading(true);
      const res = await axios.get(
        `${API}/api/admin/messages/conversation/${conversationId}`,
        { headers: getAdminHeaders() }
      );
      setMessages(res.data.messages || []);
      if (res.data.conversation) {
        setSelectedConversation((prev) => ({
          ...prev,
          ...res.data.conversation,
        }));
      }
    } catch (err) {
      console.error("Error loading messages:", err);
      if (!silent) toast.error("Failed to load messages");
    } finally {
      if (!silent) setMessagesLoading(false);
    }
  }, []);

  // Poll messages every 3s when a conversation is selected
  useEffect(() => {
    if (!selectedConversation?.id) return;
    loadMessages(selectedConversation.id);
    msgPollRef.current = setInterval(
      () => loadMessages(selectedConversation.id, true),
      3000
    );
    return () => clearInterval(msgPollRef.current);
  }, [selectedConversation?.id, loadMessages]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    setMessages([]);
    setMobileShowChat(true);
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConversation?.id || sending) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/api/admin/messages/send`,
        { conversation_id: selectedConversation.id, text: messageText.trim() },
        { headers: getAdminHeaders() }
      );
      setMessageText("");
      loadMessages(selectedConversation.id, true);
      loadConversations(true);
    } catch (err) {
      console.error("Error sending message:", err);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  // User search for new conversation
  useEffect(() => {
    if (!userSearch.trim()) {
      setUserResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setUserSearchLoading(true);
      try {
        const res = await axios.get(`${API}/api/admin/users/all`, {
          params: { search: userSearch, limit: 10, page: 1 },
          headers: getAdminHeaders(),
        });
        setUserResults(res.data.users || []);
      } catch (err) {
        console.error("Error searching users:", err);
      } finally {
        setUserSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch]);

  const handleStartConversation = async (user) => {
    try {
      const res = await axios.post(
        `${API}/api/admin/messages/conversation/create`,
        { user_email: user.email },
        { headers: getAdminHeaders() }
      );
      setShowNewConvoModal(false);
      setUserSearch("");
      setUserResults([]);
      const conv = res.data.conversation;
      setSelectedConversation(conv);
      setMobileShowChat(true);
      loadConversations(true);
      if (res.data.created) {
        toast.success(`Conversation started with ${user.full_name || user.email}`);
      }
    } catch (err) {
      console.error("Error creating conversation:", err);
      toast.error("Failed to create conversation");
    }
  };

  const getInitials = (name) => {
    if (!name || name === "Unknown User") return "?";
    return name
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatTime = (conv) => {
    if (conv.display_time && conv.display_date) {
      const today = new Date();
      const msgDate = conv.display_date;
      const todayStr = today.toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
      return msgDate === todayStr ? conv.display_time : conv.display_date;
    }
    return "";
  };

  // Total unread count for page header badge
  const totalUnread = conversations.reduce(
    (sum, c) => sum + (c.unread_count || 0),
    0
  );

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Page header */}
      <header className="sz-header flex items-center justify-between gap-4 flex-none">
        <div className="flex items-center gap-3">
          <h1 className="sz-h1 m-0">Messages</h1>
          {totalUnread > 0 && (
            <span
              className="font-[Poppins] font-bold rounded-full"
              style={{
                fontSize: "11.5px",
                color: "#78287C",
                background: "#EDE4F4",
                padding: "4px 10px",
              }}
            >
              {totalUnread} unread
            </span>
          )}
        </div>
        <button
          onClick={() => setShowNewConvoModal(true)}
          className="sz-btn sz-btn-primary"
        >
          <Plus className="w-[15px] h-[15px]" strokeWidth={2.2} />
          New Conversation
        </button>
      </header>

      {/* Two-column body */}
      <div className="flex-1 flex min-h-0">
        {/* Left: Conversation list */}
        <div
          className={`szscroll w-full md:w-[340px] md:flex-none border-r border-[#E6E9EF] bg-white overflow-y-auto ${
            mobileShowChat ? "hidden md:block" : "block"
          }`}
        >
          {/* Search (sticky) */}
          <div className="sticky top-0 z-[1] bg-white border-b border-[#F1F3F7] px-4 py-3.5">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 w-[15px] h-[15px] text-[#A8AEBC]"
                strokeWidth={2}
              />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations…"
                className="sz-input pl-[33px]"
              />
            </div>
          </div>

          {/* Conversation list */}
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader className="w-6 h-6 animate-spin text-[#78287C]" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-10 text-[#8A90A2] text-sm">
              No conversations yet
            </div>
          ) : (
            conversations.map((conv) => {
              const active = selectedConversation?.id === conv.id;
              return (
                <button
                  key={conv.id}
                  onClick={() => handleSelectConversation(conv)}
                  className="w-full flex items-center gap-3 px-4 py-[13px] cursor-pointer border-b border-[#F5F6F9] text-left transition-colors hover:bg-[#F7F8FB]"
                  style={{
                    background: active ? "#FAF7FC" : "#fff",
                    borderLeft: `3px solid ${active ? "#78287C" : "transparent"}`,
                  }}
                >
                  <div
                    className="w-[42px] h-[42px] rounded-full flex items-center justify-center font-[Poppins] font-bold text-white flex-none overflow-hidden"
                    style={{
                      background: avatarColor(
                        conv.other_person?.email || conv.other_person?.name
                      ),
                      fontSize: "13px",
                    }}
                  >
                    {conv.other_person?.profile_photo_url ? (
                      <img
                        src={conv.other_person.profile_photo_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      getInitials(conv.other_person?.name)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13px] font-semibold truncate text-[#0C1120]">
                        {conv.other_person?.name || "Unknown"}
                      </span>
                      <span className="text-[10.5px] text-[#A8AEBC] flex-none">
                        {formatTime(conv)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-[3px]">
                      <span className="text-[12px] text-[#8A90A2] truncate">
                        {conv.last_message || "No messages yet"}
                      </span>
                      {conv.unread_count > 0 && (
                        <span
                          className="font-[Poppins] font-bold text-white flex-none inline-flex items-center justify-center"
                          style={{
                            minWidth: "18px",
                            height: "18px",
                            padding: "0 5px",
                            borderRadius: "999px",
                            background: "#78287C",
                            fontSize: "10px",
                          }}
                        >
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Right: Chat area */}
        <div
          className={`flex-1 min-w-0 flex flex-col bg-[#F5F6F9] ${
            mobileShowChat
              ? "fixed inset-0 z-[120] md:static"
              : "hidden md:flex"
          }`}
        >
          {!selectedConversation ? (
            <div className="flex-1 flex items-center justify-center text-[#8A90A2]">
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-white border border-[#E6E9EF] flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-9 h-9 text-[#A8AEBC]" />
                </div>
                <h3 className="sz-h3 text-[#2B3142] mb-1">
                  No conversation selected
                </h3>
                <p className="text-[13px] text-[#8A90A2] mb-4">
                  Pick a conversation from the list or start a new one
                </p>
                <button
                  onClick={() => setShowNewConvoModal(true)}
                  className="sz-btn sz-btn-primary mx-auto"
                >
                  <Plus className="w-[15px] h-[15px]" strokeWidth={2.2} />
                  New Conversation
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div className="flex items-center gap-3 px-5 py-3.5 bg-white border-b border-[#E6E9EF] flex-none">
                <button
                  onClick={() => setMobileShowChat(false)}
                  className="md:hidden w-[34px] h-[34px] border border-[#E0E3EB] rounded-[9px] bg-white flex items-center justify-center text-[#2B3142] flex-none"
                >
                  <ArrowLeft className="w-[17px] h-[17px]" strokeWidth={2} />
                </button>
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-[Poppins] font-bold text-white flex-none overflow-hidden"
                  style={{
                    background: avatarColor(
                      selectedConversation.other_person?.email ||
                        selectedConversation.other_person?.name
                    ),
                    fontSize: "12px",
                  }}
                >
                  {selectedConversation.other_person?.profile_photo_url ? (
                    <img
                      src={selectedConversation.other_person.profile_photo_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    getInitials(selectedConversation.other_person?.name)
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold truncate text-[#0C1120]">
                    {selectedConversation.other_person?.name || "Unknown"}
                  </div>
                  <div className="text-[11.5px] text-[#8A90A2] truncate">
                    {selectedConversation.other_person?.email}
                    {selectedConversation.other_person?.role && (
                      <>
                        {selectedConversation.other_person?.email && " · "}
                        <span
                          className="font-semibold"
                          style={{
                            color:
                              ROLE_COLORS[
                                selectedConversation.other_person.role
                              ] || "#2B3142",
                          }}
                        >
                          {selectedConversation.other_person.role}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div
                ref={messagesContainerRef}
                className="szscroll flex-1 overflow-y-auto p-5 flex flex-col gap-3"
              >
                {messagesLoading ? (
                  <div className="flex justify-center py-10">
                    <Loader className="w-6 h-6 animate-spin text-[#78287C]" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-10 text-[#8A90A2] text-sm">
                    No messages yet. Send the first message!
                  </div>
                ) : (
                  messages.map((msg, index) => {
                    const isAdmin = msg.sender_id === "shootzu-admin";
                    const showDateSeparator =
                      index === 0 ||
                      (msg.display_date &&
                        messages[index - 1]?.display_date !== msg.display_date);
                    return (
                      <div key={msg.id}>
                        {showDateSeparator && msg.display_date && (
                          <div className="flex items-center justify-center my-3">
                            <span className="bg-white border border-[#E6E9EF] text-[#8A90A2] rounded-full text-[11px] px-3 py-1">
                              {msg.display_date}
                            </span>
                          </div>
                        )}
                        <div
                          className="flex"
                          style={{
                            justifyContent: isAdmin ? "flex-end" : "flex-start",
                          }}
                        >
                          <div className="max-w-[74%]">
                            <div
                              className="text-[13px] leading-[1.5] whitespace-pre-wrap break-words"
                              style={{
                                padding: "11px 14px",
                                background: isAdmin ? "#0C1120" : "#fff",
                                color: isAdmin ? "#fff" : "#0C1120",
                                border: isAdmin
                                  ? "none"
                                  : "1px solid #E6E9EF",
                                borderRadius: isAdmin
                                  ? "14px 14px 4px 14px"
                                  : "14px 14px 14px 4px",
                              }}
                            >
                              {linkifyText(msg.text)}
                            </div>
                            <div
                              className="text-[10.5px] text-[#A8AEBC] mt-1"
                              style={{ textAlign: isAdmin ? "right" : "left" }}
                            >
                              {msg.display_time || ""}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="flex items-end gap-2.5 px-5 py-3.5 bg-white border-t border-[#E6E9EF] flex-none">
                <AutoGrowTextarea
                  placeholder="Type a message…"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onSubmit={handleSendMessage}
                  maxRows={5}
                  disabled={sending}
                  className="flex-1 px-[13px] py-[11px] border border-[#E0E3EB] rounded-[12px] text-[13px] text-[#0C1120] bg-white outline-none focus:border-[#28C0D0] focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-[#A8AEBC]"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!messageText.trim() || sending}
                  className="w-[42px] h-[42px] rounded-[12px] bg-[#78287C] hover:bg-[#572B7C] text-white flex items-center justify-center flex-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <Loader className="w-[18px] h-[18px] animate-spin" />
                  ) : (
                    <Send className="w-[18px] h-[18px]" strokeWidth={2} />
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* New Conversation Modal */}
      {showNewConvoModal && (
        <div
          className="sz-modal-backdrop"
          onClick={() => setShowNewConvoModal(false)}
        >
          <div
            className="sz-modal-card w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E6E9EF]">
              <h3 className="sz-h3 m-0">New Conversation</h3>
              <button
                onClick={() => setShowNewConvoModal(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-[#8A90A2] hover:bg-[#F1F3F7]"
              >
                <X className="w-[18px] h-[18px]" />
              </button>
            </div>

            {/* Modal body */}
            <div className="p-5 flex flex-col gap-4 overflow-hidden">
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-[15px] h-[15px] text-[#A8AEBC]"
                  strokeWidth={2}
                />
                <input
                  placeholder="Search by name or email…"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="sz-input pl-[33px]"
                  autoFocus
                />
              </div>

              <div className="szscroll max-h-64 overflow-y-auto flex flex-col gap-1">
                {userSearchLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader className="w-5 h-5 animate-spin text-[#78287C]" />
                  </div>
                ) : userResults.length === 0 && userSearch.trim() ? (
                  <p className="text-sm text-[#8A90A2] text-center py-4">
                    No users found
                  </p>
                ) : (
                  userResults.map((user) => {
                    const displayName =
                      user.full_name ||
                      `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
                      user.email;
                    return (
                      <button
                        key={user.id || user.email}
                        onClick={() => handleStartConversation(user)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-[11px] hover:bg-[#F7F8FB] transition-colors text-left"
                      >
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center font-[Poppins] font-bold text-white flex-none overflow-hidden"
                          style={{
                            background: avatarColor(user.email || displayName),
                            fontSize: "12px",
                          }}
                        >
                          {user.profile_photo_url ? (
                            <img
                              src={user.profile_photo_url}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <User className="w-4 h-4" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-semibold truncate text-[#0C1120]">
                            {displayName}
                          </p>
                          <p className="text-[11.5px] text-[#8A90A2] truncate">
                            {user.email}
                          </p>
                        </div>
                        {user.role && (
                          <span
                            className="text-[10.5px] font-semibold flex-none"
                            style={{
                              color: ROLE_COLORS[user.role] || "#8A90A2",
                            }}
                          >
                            {user.role}
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
